/**
 * Frontend validation utilities for Indian phone numbers and other common validations
 */

/**
 * Validates Indian mobile numbers
 * Indian mobile numbers should:
 * - Start with +91
 * - Have exactly 10 digits after the country code
 * - First digit should be 6, 7, 8, or 9
 * @param {string} mobile - The mobile number to validate
 * @returns {boolean} - True if valid Indian mobile number
 */
export const isValidIndianMobile = (mobile) => {
  // Remove any spaces or special characters except +
  const cleaned = mobile.replace(/[\s-]/g, '');
  
  // Check if it starts with +91
  if (!cleaned.startsWith('+91')) {
    return false;
  }
  
  // Extract the number part (after +91)
  const numberPart = cleaned.substring(3);
  
  // Check if it has exactly 10 digits
  if (numberPart.length !== 10) {
    return false;
  }
  
  // Check if all characters are digits
  if (!/^\d+$/.test(numberPart)) {
    return false;
  }
  
  // Check if first digit is 6, 7, 8, or 9
  const firstDigit = parseInt(numberPart[0]);
  if (![6, 7, 8, 9].includes(firstDigit)) {
    return false;
  }
  
  return true;
};

/**
 * Validates Indian mobile number without country code
 * @param {string} mobile - The mobile number to validate (without +91)
 * @returns {boolean} - True if valid Indian mobile number
 */
export const isValidIndianMobileWithoutCountryCode = (mobile) => {
  // Remove any spaces or special characters
  const cleaned = mobile.replace(/[\s-]/g, '');
  
  // Check if it has exactly 10 digits
  if (cleaned.length !== 10) {
    return false;
  }
  
  // Check if all characters are digits
  if (!/^\d+$/.test(cleaned)) {
    return false;
  }
  
  // Check if first digit is 6, 7, 8, or 9
  const firstDigit = parseInt(cleaned[0]);
  if (![6, 7, 8, 9].includes(firstDigit)) {
    return false;
  }
  
  return true;
};

/**
 * Formats Indian mobile number with country code
 * @param {string} mobile - The mobile number to format
 * @returns {string} - Formatted mobile number with +91
 */
export const formatIndianMobile = (mobile) => {
  // Remove any spaces or special characters
  const cleaned = mobile.replace(/[\s-]/g, '');
  
  // If it already starts with +91, return as is
  if (cleaned.startsWith('+91')) {
    return cleaned;
  }
  
  // If it's a 10-digit number, add +91
  if (cleaned.length === 10 && /^\d+$/.test(cleaned)) {
    return `+91${cleaned}`;
  }
  
  // If it's an 11-digit number starting with 0, remove the 0 and add +91
  if (cleaned.length === 11 && cleaned.startsWith('0') && /^\d+$/.test(cleaned)) {
    return `+91${cleaned.substring(1)}`;
  }
  
  // If it's a 12-digit number starting with 91, add +
  if (cleaned.length === 12 && cleaned.startsWith('91') && /^\d+$/.test(cleaned)) {
    return `+${cleaned}`;
  }
  
  return cleaned;
};

/**
 * Gets the error message for invalid Indian mobile number
 * @param {string} mobile - The mobile number that was invalid
 * @returns {string} - Error message
 */
export const getIndianMobileErrorMessage = (mobile) => {
  const cleaned = mobile.replace(/[\s-]/g, '');
  
  if (!cleaned.startsWith('+91')) {
    return 'Please enter a valid mobile';
  }
  
  const numberPart = cleaned.substring(3);
  
  if (numberPart.length !== 10) {
    return 'Please enter a valid mobile';
  }
  
  if (!/^\d+$/.test(numberPart)) {
    return 'Please enter a valid mobile';
  }
  
  const firstDigit = parseInt(numberPart[0]);
  if (![6, 7, 8, 9].includes(firstDigit)) {
    return 'Please enter a valid mobile';
  }
  
  return 'Please enter a valid mobile';
};

/**
 * Validates email format
 * @param {string} email - The email to validate
 * @returns {boolean} - True if valid email
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates name (minimum 2 characters, no special characters)
 * @param {string} name - The name to validate
 * @returns {boolean} - True if valid name
 */
export const isValidName = (name) => {
  return name && name.trim().length >= 2 && /^[a-zA-Z\s]+$/.test(name.trim());
};

/**
 * Formats phone number for display (adds spaces for readability)
 * @param {string} phone - The phone number to format
 * @returns {string} - Formatted phone number
 */
export const formatPhoneForDisplay = (phone) => {
  if (!phone) return '';
  
  // If it's a 10-digit number, format as +91 XXXXX XXXXX
  if (phone.length === 10 && /^\d+$/.test(phone)) {
    return `+91 ${phone.slice(0, 5)} ${phone.slice(5)}`;
  }
  
  // If it's +91 followed by 10 digits, format as +91 XXXXX XXXXX
  if (phone.startsWith('+91') && phone.length === 13) {
    const numberPart = phone.substring(3);
    return `+91 ${numberPart.slice(0, 5)} ${numberPart.slice(5)}`;
  }
  
  return phone;
};
